import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
    Container, Row, Col, Card, CardBody, Spinner, Progress,
} from "reactstrap";
import CountUp from "react-countup";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { apiV2PostConfig } from "../../helpers/api_helper";

/* ── interfaces ── */
interface ServiceItem {
    contrat: { ref: string };
    service: {
        label: string;
        description?: string;
        statut_service: string;
        date_ouverture?: string;
        date_ouverture_prevue?: string;
        qty?: number | string;
        categorie_id?: number | string;
        categorie_label?: string;
        extrafields?: Record<string, any>;
    };
}

/* ── helpers ── */
const formatDate = (raw?: string) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d.getTime())) return "—";
    return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

/* Catégorisation : on regroupe les services par "type d'offre" */
type OfferKey = "mobile" | "voix" | "liens" | "cyber" | "equipement" | "autre";

interface OfferDef {
    key: OfferKey;
    label: string;       // libellé affiché ("Flotte Mobile", etc.)
    unitSingular: string; // "ligne", "service", "lien"
    unitPlural: string;
    icon: string;
    color: string;       // bootstrap color name
    gradient: string;    // CSS gradient
    link: string;        // page de gestion dédiée
}

const OFFERS: OfferDef[] = [
    {
        key: "mobile",
        label: "Flotte Mobile",
        unitSingular: "ligne",
        unitPlural: "lignes",
        icon: "ri-smartphone-line",
        color: "info",
        gradient: "linear-gradient(135deg, #0EA5E9 0%, #06B6D4 100%)",
        link: "/flotte-mobile",
    },
    {
        key: "voix",
        label: "Téléphonie Fixe",
        unitSingular: "service",
        unitPlural: "services",
        icon: "ri-phone-line",
        color: "primary",
        gradient: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
        link: "/telephonie-fixe",
    },
    {
        key: "liens",
        label: "Liens Internet",
        unitSingular: "lien",
        unitPlural: "liens",
        icon: "ri-global-line",
        color: "success",
        gradient: "linear-gradient(135deg, #10B981 0%, #34D399 100%)",
        link: "/mes-liens",
    },
    {
        key: "cyber",
        label: "Cybersécurité",
        unitSingular: "service",
        unitPlural: "services",
        icon: "ri-shield-check-line",
        color: "danger",
        gradient: "linear-gradient(135deg, #EF4444 0%, #F97316 100%)",
        link: "/services",
    },
    {
        key: "equipement",
        label: "Équipements",
        unitSingular: "équipement",
        unitPlural: "équipements",
        icon: "ri-router-line",
        color: "warning",
        gradient: "linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)",
        link: "/services",
    },
    {
        key: "autre",
        label: "Autres services",
        unitSingular: "service",
        unitPlural: "services",
        icon: "ri-apps-2-line",
        color: "secondary",
        gradient: "linear-gradient(135deg, #64748B 0%, #94A3B8 100%)",
        link: "/services",
    },
];

/* Détecte à quelle "offre" appartient un service */
const detectOfferKey = (s: ServiceItem["service"]): OfferKey => {
    const id = Number(s.categorie_id);
    if (id === 7) return "liens";
    if (id === 9) return "voix";

    const lbl = (s.categorie_label || "").toLowerCase().trim();
    if (lbl === "liens" || lbl.includes("internet") || lbl.includes("connectivité")) return "liens";
    if (lbl === "voix" || lbl.includes("téléphonie fixe") || lbl.includes("telephonie fixe")) return "voix";
    if (lbl === "mobiles" || lbl === "mobile" || lbl.includes("mobile")) return "mobile";
    if (lbl.includes("cyber") || lbl.includes("sécur")) return "cyber";
    if (lbl.includes("équipement") || lbl.includes("equipement") || lbl.includes("matériel") || lbl.includes("materiel")) return "equipement";

    /* Heuristique secondaire via SDA (mobiles 06/07) */
    const sda = (s.extrafields?.sda || "").replace(/\s+/g, "");
    if (/^0?[67]\d{8}$/.test(sda)) return "mobile";

    return "autre";
};

/* ── composant ── */
const Services = () => {
    const [services, setServices] = useState<ServiceItem[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res: any = await axios.post("/apiV2/", { action: "services" }, apiV2PostConfig());
                setServices(res?.data ?? res ?? []);
            } catch {
                /* silencieux */
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    /* ── KPI globaux ── */
    const total = services.length;
    const actifs = services.filter((x) => x.service.statut_service === "Actif").length;
    const inactifs = services.filter((x) => x.service.statut_service === "Inactif").length;
    const enCours = total - actifs - inactifs;
    const contratsUniques = useMemo(
        () => new Set(services.map((s) => s.contrat?.ref).filter(Boolean)).size,
        [services]
    );

    const widgets = [
        { label: "Total services", counter: total, icon: "ri-file-list-3-line", color: "primary" },
        { label: "Services actifs", counter: actifs, icon: "ri-checkbox-circle-line", color: "success" },
        { label: "Services inactifs", counter: inactifs, icon: "ri-close-circle-line", color: "danger" },
        { label: "Contrats", counter: contratsUniques, icon: "ri-folder-2-line", color: "info" },
    ];

    /* ── Stats par offre ── */
    interface OfferStats {
        def: OfferDef;
        total: number;
        actifs: number;
        inactifs: number;
        enCours: number;
        contratsUniques: number;
        lastDate: Date | null;
    }

    const offerStats: OfferStats[] = useMemo(() => {
        const buckets: Record<OfferKey, ServiceItem[]> = {
            mobile: [], voix: [], liens: [], cyber: [], equipement: [], autre: [],
        };
        services.forEach((s) => buckets[detectOfferKey(s.service)].push(s));

        return OFFERS.map((def) => {
            const arr = buckets[def.key];
            const a = arr.filter((x) => x.service.statut_service === "Actif").length;
            const i = arr.filter((x) => x.service.statut_service === "Inactif").length;
            const dates = arr
                .map((x) => x.service.date_ouverture)
                .filter((d): d is string => !!d)
                .map((d) => new Date(d))
                .filter((d) => !isNaN(d.getTime()));
            const lastDate = dates.length ? new Date(Math.max(...dates.map((d) => d.getTime()))) : null;
            return {
                def,
                total: arr.length,
                actifs: a,
                inactifs: i,
                enCours: arr.length - a - i,
                contratsUniques: new Set(arr.map((x) => x.contrat?.ref).filter(Boolean)).size,
                lastDate,
            };
        });
    }, [services]);

    /* On masque les offres totalement vides pour ne pas polluer la vue */
    const visibleOffers = offerStats.filter((o) => o.total > 0);

    /* Activité récente : 5 derniers services ouverts (actifs) */
    const recent = useMemo(() => {
        return [...services]
            .filter((x) => x.service.date_ouverture)
            .sort((a, b) => {
                const da = new Date(a.service.date_ouverture!).getTime();
                const db = new Date(b.service.date_ouverture!).getTime();
                return db - da;
            })
            .slice(0, 5);
    }, [services]);

    /* Taux d'activité global */
    const tauxActivite = total > 0 ? Math.round((actifs / total) * 100) : 0;

    /* ── loading ── */
    if (loading) {
        return (
            <div className="page-content d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <div className="text-center">
                    <Spinner style={{ width: "3rem", height: "3rem", color: "#0066A1" }} />
                    <p className="text-muted mt-3 fw-medium">Chargement de vos services…</p>
                </div>
            </div>
        );
    }

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Mes Services" pageTitle="Espace Client" />

                    {/* ── Widgets KPI globaux ── */}
                    <Row>
                        {widgets.map((w, i) => (
                            <Col xxl={3} sm={6} key={i}>
                                <Card className="card-animate">
                                    <CardBody>
                                        <div className="d-flex justify-content-between">
                                            <div>
                                                <p className="fw-medium text-muted mb-0">{w.label}</p>
                                                <h2 className="mt-4 ff-secondary fw-semibold">
                                                    <CountUp start={0} end={w.counter} duration={2} />
                                                </h2>
                                            </div>
                                            <div className="avatar-sm flex-shrink-0">
                                                <span className={`avatar-title rounded-circle fs-4 bg-${w.color}-subtle text-${w.color}`}>
                                                    <i className={w.icon}></i>
                                                </span>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        ))}
                    </Row>

                    {/* ── Taux d'activité ── */}
                    {total > 0 && (
                        <Row>
                            <Col lg={12}>
                                <Card className="card-animate">
                                    <CardBody>
                                        <div className="d-flex align-items-center justify-content-between mb-2">
                                            <div>
                                                <h6 className="text-muted text-uppercase fs-13 mb-1">
                                                    <i className="ri-pulse-line me-1"></i>Taux d'activité global
                                                </h6>
                                                <p className="mb-0 text-muted">
                                                    {actifs} service{actifs > 1 ? "s" : ""} actif{actifs > 1 ? "s" : ""} sur {total}
                                                </p>
                                            </div>
                                            <h3 className="mb-0 fw-semibold text-success">
                                                <CountUp start={0} end={tauxActivite} duration={2} suffix=" %" />
                                            </h3>
                                        </div>
                                        <Progress
                                            value={tauxActivite}
                                            color={tauxActivite >= 75 ? "success" : tauxActivite >= 40 ? "warning" : "danger"}
                                            style={{ height: 10, borderRadius: 8 }}
                                        />
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* ── Mes offres ── */}
                    <Row className="mt-2">
                        <Col xs={12}>
                            <h5 className="mb-3 fw-semibold">
                                <i className="ri-stack-line me-2 text-primary"></i>Mes offres
                            </h5>
                        </Col>

                        {visibleOffers.length === 0 ? (
                            <Col xs={12}>
                                <Card>
                                    <CardBody className="text-center py-5">
                                        <div className="avatar-md mx-auto mb-3">
                                            <span className="avatar-title rounded-circle bg-primary-subtle text-primary fs-3">
                                                <i className="ri-file-list-3-line"></i>
                                            </span>
                                        </div>
                                        <h5 className="mt-2">Aucun service</h5>
                                        <p className="text-muted">Vous n'avez pas encore de service actif.</p>
                                    </CardBody>
                                </Card>
                            </Col>
                        ) : (
                            visibleOffers.map((o) => {
                                const pct = o.total > 0 ? Math.round((o.actifs / o.total) * 100) : 0;
                                const unit = o.actifs > 1 ? o.def.unitPlural : o.def.unitSingular;
                                return (
                                    <Col xxl={4} lg={6} md={6} key={o.def.key}>
                                        <Card className="card-animate offer-card h-100 border-0 shadow-sm">
                                            <div
                                                className="offer-card-header text-white"
                                                style={{ background: o.def.gradient }}
                                            >
                                                <div className="d-flex align-items-center">
                                                    <div className="offer-card-icon">
                                                        <i className={o.def.icon}></i>
                                                    </div>
                                                    <div className="flex-grow-1 ms-3">
                                                        <h5 className="text-white mb-0 fw-bold">{o.def.label}</h5>
                                                        <small className="text-white-50">
                                                            {o.contratsUniques} contrat{o.contratsUniques > 1 ? "s" : ""}
                                                        </small>
                                                    </div>
                                                    <div className="text-end">
                                                        <div className="offer-big-count">
                                                            <CountUp start={0} end={o.actifs} duration={1.5} />
                                                        </div>
                                                        <small className="text-white-50">
                                                            {unit} actif{o.actifs > 1 ? "s" : ""}
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>

                                            <CardBody>
                                                <div className="d-flex justify-content-between text-muted fs-13 mb-2">
                                                    <span>
                                                        <i className="ri-checkbox-circle-fill text-success me-1"></i>
                                                        {o.actifs} actif{o.actifs > 1 ? "s" : ""}
                                                    </span>
                                                    {o.inactifs > 0 && (
                                                        <span>
                                                            <i className="ri-close-circle-fill text-danger me-1"></i>
                                                            {o.inactifs} inactif{o.inactifs > 1 ? "s" : ""}
                                                        </span>
                                                    )}
                                                    {o.enCours > 0 && (
                                                        <span>
                                                            <i className="ri-time-fill text-warning me-1"></i>
                                                            {o.enCours} en cours
                                                        </span>
                                                    )}
                                                </div>

                                                <Progress
                                                    value={pct}
                                                    color={o.def.color}
                                                    style={{ height: 6, borderRadius: 6 }}
                                                />

                                                <div className="d-flex justify-content-between align-items-center mt-3">
                                                    <small className="text-muted">
                                                        {o.lastDate ? (
                                                            <>
                                                                <i className="ri-calendar-check-line me-1"></i>
                                                                Dernière activation&nbsp;: {formatDate(o.lastDate.toISOString())}
                                                            </>
                                                        ) : (
                                                            <span className="text-muted">—</span>
                                                        )}
                                                    </small>
                                                    <Link
                                                        to={o.def.link}
                                                        className={`btn btn-soft-${o.def.color} btn-sm`}
                                                    >
                                                        Gérer <i className="ri-arrow-right-line ms-1"></i>
                                                    </Link>
                                                </div>
                                            </CardBody>
                                        </Card>
                                    </Col>
                                );
                            })
                        )}
                    </Row>

                    {/* ── Activité récente ── */}
                    {recent.length > 0 && (
                        <Row className="mt-2">
                            <Col xs={12}>
                                <h5 className="mb-3 fw-semibold">
                                    <i className="ri-history-line me-2 text-primary"></i>Activations récentes
                                </h5>
                            </Col>
                            <Col xs={12}>
                                <Card>
                                    <CardBody className="py-2">
                                        <div className="recent-list">
                                            {recent.map((item, idx) => {
                                                const offer = OFFERS.find((o) => o.key === detectOfferKey(item.service))!;
                                                return (
                                                    <div
                                                        key={idx}
                                                        className="d-flex align-items-center py-3 recent-item"
                                                    >
                                                        <div
                                                            className="recent-icon flex-shrink-0"
                                                            style={{ background: offer.gradient }}
                                                        >
                                                            <i className={offer.icon}></i>
                                                        </div>
                                                        <div className="flex-grow-1 ms-3 min-w-0">
                                                            <div className="fw-medium text-truncate">
                                                                {item.service.label}
                                                                {Number(item.service.qty ?? 1) > 1 && (
                                                                    <span className="text-muted ms-1">
                                                                        ({Number(item.service.qty)})
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <small className="text-muted">
                                                                <i className="ri-file-list-3-line me-1"></i>
                                                                {item.contrat.ref}
                                                                <span className="mx-2">•</span>
                                                                {offer.label}
                                                            </small>
                                                        </div>
                                                        <div className="text-end flex-shrink-0">
                                                            <small className="text-muted d-block">
                                                                {formatDate(item.service.date_ouverture)}
                                                            </small>
                                                            {item.service.statut_service === "Actif" ? (
                                                                <span className="badge bg-success-subtle text-success mt-1">
                                                                    <i className="ri-checkbox-circle-fill me-1"></i>Actif
                                                                </span>
                                                            ) : (
                                                                <span className="badge bg-danger-subtle text-danger mt-1">
                                                                    <i className="ri-time-fill me-1"></i>
                                                                    {item.service.statut_service}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </Container>

                <style>{`
                    .offer-card-header {
                        padding: 1.25rem 1.25rem;
                        border-radius: 0.5rem 0.5rem 0 0;
                        position: relative;
                        overflow: hidden;
                    }
                    .offer-card-header::before {
                        content: '';
                        position: absolute;
                        top: -40%;
                        right: -10%;
                        width: 180px;
                        height: 180px;
                        background: rgba(255, 255, 255, 0.12);
                        border-radius: 50%;
                    }
                    .offer-card-icon {
                        width: 48px;
                        height: 48px;
                        border-radius: 12px;
                        background: rgba(255, 255, 255, 0.22);
                        backdrop-filter: blur(8px);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 22px;
                        color: white;
                        flex-shrink: 0;
                        z-index: 1;
                    }
                    .offer-big-count {
                        font-size: 2rem;
                        font-weight: 700;
                        line-height: 1;
                        color: white;
                        z-index: 1;
                        position: relative;
                    }
                    .offer-card {
                        transition: transform 0.25s ease, box-shadow 0.25s ease;
                    }
                    .offer-card:hover {
                        transform: translateY(-4px);
                        box-shadow: 0 10px 24px rgba(0, 0, 0, 0.08) !important;
                    }
                    .recent-icon {
                        width: 42px;
                        height: 42px;
                        border-radius: 10px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: white;
                        font-size: 18px;
                    }
                    .recent-item + .recent-item {
                        border-top: 1px dashed #e9ecef;
                    }
                    .min-w-0 {
                        min-width: 0;
                    }
                `}</style>
            </div>
        </React.Fragment>
    );
};

export default Services;
